import React, { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

/**
 * FinderKidZonePage — the "Kid Zone" tile on HustleFinder lands here.
 *
 * Bundles two things families actually need on a day out:
 *   1. A shortcut into the search filtered to kid-friendly places
 *      (playgrounds, parks, libraries) — one tap.
 *   2. Interactive/printable activities for the road:
 *      Mazes + Colouring pages. (Sudoku / Crosswords live on
 *      /finder/puzzles for older kids + grown-ups.)
 */
const KID_ACTIVITIES = [
  {
    key: "mazes",
    to: "/finder/kids/mazes",
    title: "Mazes",
    desc: "Kid, Family and Hard mazes plus themed treasure hunts. Play on-screen or print A4.",
    icon: "🌀",
    testid: "kids-card-mazes",
  },
  {
    key: "jigsaw",
    to: "/finder/kids/jigsaw",
    title: "Jigsaw pictures",
    desc: "Drag-to-solve picture puzzles — koala, kangaroo, Uluru, Opera House, Bondi, Reef.",
    icon: "🧩",
    testid: "kids-card-jigsaw",
  },
  {
    key: "colouring",
    to: "/finder/kids/colouring",
    title: "Colouring pages",
    desc: "Aussie-themed line art — koalas, kangaroos, beaches. Print A4.",
    icon: "🎨",
    testid: "kids-card-colouring",
  },
];

export default function FinderKidZonePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  useEffect(() => {
    document.title = t("finder.kids.meta_title",
      { defaultValue: "Kid Zone — HustleFinder" });
  }, [t]);

  return (
    <div
      data-testid="kids-page"
      className="min-h-screen bg-gradient-to-b from-slate-50 to-white"
    >
      <div className="max-w-2xl mx-auto px-4 py-10 text-center">
        <Link
          to="/finder"
          data-testid="kids-back-link"
          className="text-xs text-emerald-700 hover:underline"
        >
          ← {t("finder.kids.back", { defaultValue: "back to search" })}
        </Link>
        <h1 className="text-3xl font-bold text-slate-900 mt-3 mb-2">
          {t("finder.kids.heading", { defaultValue: "Kid Zone" })}
        </h1>
        <p className="text-slate-600 mb-8">
          {t("finder.kids.intro", { defaultValue:
            "Nearest playgrounds, parks and libraries — plus activities for the car ride. " +
            "Everything works offline once loaded." })}
        </p>

        {/* Kid-friendly places — one-tap into the finder */}
        <button
          onClick={() => navigate("/finder?cat=kid_zone")}
          data-testid="kids-find-places-btn"
          className="w-full text-left bg-emerald-600 hover:bg-emerald-700
                     text-white rounded-2xl p-5 mb-4 transition"
        >
          <div className="flex items-baseline gap-2 mb-1">
            <span className="text-2xl">🛝</span>
            <h2 className="text-lg font-semibold">
              {t("finder.kids.find_places_title",
                { defaultValue: "Find kid-friendly places nearby" })}
            </h2>
          </div>
          <p className="text-sm text-emerald-50">
            {t("finder.kids.find_places_desc", { defaultValue:
              "Playgrounds, dog parks, libraries and sports fields — sorted by distance." })}
          </p>
        </button>

        {/* Activities — now live */}
        <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wide
                       text-left mb-2 mt-6">
          {t("finder.kids.activities_heading",
            { defaultValue: "Activities" })}
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {KID_ACTIVITIES.map((c) => (
            <Link
              key={c.key}
              to={c.to}
              data-testid={c.testid}
              className="text-left bg-white border border-slate-200 rounded-2xl p-5
                          hover:border-emerald-400 hover:shadow-md transition"
            >
              <div className="flex items-baseline gap-2 mb-1">
                <span className="text-2xl">{c.icon}</span>
                <h2 className="text-lg font-semibold text-slate-900">
                  {c.title}
                </h2>
                <span className="ml-auto text-[10px] uppercase tracking-widest
                                  bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full">
                  Play
                </span>
              </div>
              <p className="text-sm text-slate-600">{c.desc}</p>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}
